import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Video, ChevronRight, Home, LogOut, User, Shield } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

export default function Header({ projectTitle, reviewTitle, projectId }) {
    const { currentUser, userRole, logout } = useAuth();
    const [showMenu, setShowMenu] = useState(false);

    const handleLogout = async () => {
        try {
            await logout();
        } catch {
            console.error("Failed to log out");
        }
    };

    return (
        <div className="mb-8">
            {/* Banner */}
            <div className="relative rounded-none px-8 py-16 -mx-6 -mt-6 mb-6 overflow-hidden min-h-[280px]">
                {/* Gradient Overlay */}
                <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/25 to-black/40 z-10 pointer-events-none transition-colors duration-400"></div>
                <div className="absolute -bottom-8 left-0 right-0 h-16 bg-gradient-to-b from-transparent via-black/40 to-[#0a0a0a] z-20 pointer-events-none"></div>

                {/* Background Image */}
                <div className="absolute inset-0 z-0 overflow-hidden">
                    <img
                        src="/banner.jpg"
                        alt="Banner"
                        className="w-full h-full object-cover opacity-65 brightness-90 saturate-110"
                        onError={(e) => {
                            e.target.style.display = 'none'; // Fallback if image missing
                            e.target.parentNode.style.backgroundColor = '#1a1a1a';
                        }}
                    />
                </div>

                {/* Top Bar (Login/Logout) */}
                <div className="absolute top-6 right-6 z-40">
                    {currentUser ? (
                        <div className="relative">
                            <button
                                onClick={() => setShowMenu(!showMenu)}
                                className="flex items-center gap-2 bg-black/40 hover:bg-black/60 border border-white/10 px-4 py-2 rounded-full text-white text-sm font-medium transition-all backdrop-blur-sm"
                            >
                                <User size={16} className="text-cyan-400" />
                                <span className="max-w-[150px] truncate">{currentUser.email}</span>
                            </button>

                            {showMenu && (
                                <div className="absolute right-0 mt-2 w-48 bg-[#1a1a1a] border border-white/10 rounded-xl shadow-2xl py-2 overflow-hidden animate-in fade-in zoom-in-95 duration-100">
                                    {userRole === 'admin' && (
                                        <Link
                                            to="/admin/users"
                                            className="flex items-center gap-2 px-4 py-2.5 text-sm text-gray-300 hover:bg-white/10 hover:text-white transition-colors"
                                            onClick={() => setShowMenu(false)}
                                        >
                                            <Shield size={16} className="text-purple-400" /> Manage Users
                                        </Link>
                                    )}
                                    <button
                                        onClick={handleLogout}
                                        className="w-full text-left flex items-center gap-2 px-4 py-2.5 text-sm text-gray-300 hover:bg-red-500/10 hover:text-red-400 transition-colors"
                                    >
                                        <LogOut size={16} /> Log Out
                                    </button>
                                </div>
                            )}
                        </div>
                    ) : (
                        <Link
                            to="/login"
                            className="flex items-center gap-2 bg-cyan-600/80 hover:bg-cyan-600 text-white px-5 py-2 rounded-full text-sm font-bold transition-all shadow-lg backdrop-blur-sm"
                        >
                            Log In
                        </Link>
                    )}
                </div>

                {/* Content */}
                <div className="relative z-30 flex flex-col items-center gap-4 text-white">
                    <div className="flex items-center gap-6 mb-4">
                        <Video className="w-16 h-16 text-cyan-400 drop-shadow-[0_4px_16px_rgba(0,102,255,0.5)]" />
                    </div>

                    <div className="text-center">
                        <h1 className="text-4xl font-bold mb-3 drop-shadow-xl">Hey Media Review</h1>
                        <p className="text-lg text-white/95 font-medium drop-shadow-md">
                            Chuyên nghiệp • Nhanh chóng • Hiệu quả
                        </p>
                    </div>
                </div>
            </div>

            {/* Breadcrumb */}
            {currentUser && (
                <div className="max-w-[1400px] mx-auto px-6">
                    <nav className="flex items-center gap-2 p-3 bg-white/5 border border-white/10 rounded-lg w-fit transition-all duration-200 hover:bg-white/10">
                        <Link
                            to="/"
                            className="flex items-center gap-2 px-3 py-1.5 text-gray-400 hover:text-gray-200 text-sm font-medium rounded-md transition-colors"
                        >
                            <Home className="w-4 h-4" />
                            Projects
                        </Link>

                        {projectTitle && (
                            <>
                                <ChevronRight className="w-4 h-4 text-gray-600" />
                                {projectId ? (
                                    <Link
                                        to={`/project/${projectId}`}
                                        className={`flex items-center gap-2 px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${!reviewTitle ? 'text-cyan-400 bg-cyan-500/10' : 'text-gray-400 hover:text-gray-200'}`}
                                    >
                                        {projectTitle}
                                    </Link>
                                ) : (
                                    <span className={`flex items-center gap-2 px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${!reviewTitle ? 'text-cyan-400 bg-cyan-500/10' : 'text-gray-400'}`}>
                                        {projectTitle}
                                    </span>
                                )}
                            </>
                        )}

                        {reviewTitle && (
                            <>
                                <ChevronRight className="w-4 h-4 text-gray-600" />
                                <span className="flex items-center gap-2 px-3 py-1.5 text-cyan-400 bg-cyan-500/10 text-sm font-medium rounded-md">
                                    {reviewTitle}
                                </span>
                            </>
                        )}
                    </nav>
                </div>
            )}
        </div>
    );
}
